import React, { useState, useEffect } from "react";
import { Modal, Form, DatePicker, Select, Input, message, Divider, Row, Col } from "antd";
import dayjs from "dayjs";
import { MemberWithPrimaryFollowup } from "../../../utils/memberFollowupUtils";
import { MemberFollowup, FollowUpStatus } from "../../../model/members/MemberFollowup";
import { MemberProfile } from "../../../model/members/MemberProfile";
import { StaffUser } from "../../../model/members/StaffUser";
import { useClientConfig } from "../../../common/client/ClientConfigProvider";
import { DEFAULT_SIGNUP_HOW_THEY_HEARD } from "../../../model/client/ClientSettings";

const LEAD_TYPE_OPTIONS = [
  { value: "ColdLead", label: "Cold Lead" },
  { value: "WarmLead", label: "Warm Lead" },
  { value: "FreeTrialBooked", label: "Free Trial Booked" },
  { value: "TourBooked", label: "Tour Booked" },
  { value: "Membership", label: "Membership" },
  { value: "DeadLead", label: "Dead Lead" },
];

const LEAD_SOURCE_OPTIONS = [
  { value: "Visitor", label: "Visitor" },
  { value: "Tour", label: "Tour" },
  { value: "Call", label: "Call" },
  { value: "CurrentMember", label: "Current Member" },
  { value: "PreviousMember", label: "Previous Member" },
  { value: "LegacyMember", label: "Legacy Member" },
];

// NOTE: howTheyHeard now reads from per-client config (signup.howTheyHeard via useClientConfig), so
// this editor offers exactly what the signup forms offer and the retention report labels (FORT-349).
// Interests is still a hardcoded copy of the defaults — unifying it onto signup.interests is the
// remaining part of that follow-up.
const INTERESTS_OPTIONS = ["Gym", "Squash", "Classes", "Steam / Sauna", "Treatments"];

const STATUS_OPTIONS: { value: FollowUpStatus; label: string }[] = [
  { value: FollowUpStatus.Prospect, label: "Prospect" },
  { value: FollowUpStatus.FollowedUp, label: "Followed Up" },
  { value: FollowUpStatus.DeclinedMembership, label: "Declined Membership" },
  { value: FollowUpStatus.ConsideringMembership, label: "Considering Membership" },
];

interface EditFollowupModalProps {
  open: boolean;
  row: MemberWithPrimaryFollowup | null;
  onClose: () => void;
  onSaved: () => void;
  onSaveFollowup: (memberId: string, followup: MemberFollowup) => Promise<void>;
  onSaveMember: (member: MemberProfile, metadata: Record<string, any>) => Promise<void>;
  staffUsers: StaffUser[];
}

const EditFollowupModal: React.FC<EditFollowupModalProps> = ({
  open,
  row,
  onClose,
  onSaved,
  onSaveFollowup,
  onSaveMember,
  staffUsers,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // Offer exactly what the signup forms offer, so staff can't create data under a retired code.
  const { signup } = useClientConfig();
  const howTheyHeardOptions = signup?.howTheyHeard ?? DEFAULT_SIGNUP_HOW_THEY_HEARD;

  const primary = row?.primaryFollowup ?? null;
  const member = row?.member;

  useEffect(() => {
    if (!open || !row) return;
    const metadata = member?.metadata || {};

    form.setFieldsValue({
      leadType: metadata.LeadType
        ? String(metadata.LeadType).replace(/\s+/g, "")
        : undefined,
      leadSource: metadata.LeadSource
        ? String(metadata.LeadSource).replace(/\s+/g, "")
        : undefined,
      interests: Array.isArray(metadata.Interests) ? metadata.Interests : [],
      howTheyHeard: metadata.HowTheyHeard
        ? String(metadata.HowTheyHeard).replace(/\s+/g, "")
        : undefined,
      referringMember: metadata.ReferringMember ?? "",
      lastContacted: primary?.followUpDate ? dayjs(primary.followUpDate) : null,
      nextFollowUpDate: primary?.nextFollowUpDate
        ? dayjs(primary.nextFollowUpDate)
        : null,
      status: primary?.status ?? FollowUpStatus.Prospect,
      notes: primary?.notes ?? "",
      contactedByStaffId: primary?.contactedByStaffId ?? undefined,
    });
  }, [open, row, primary, member, form]);

  const handleSave = async () => {
    if (!member) return;
    try {
      const values = await form.validateFields();
      setSaving(true);

      // --- Detect member metadata changes ---
      const originalMetadata = member.metadata || {};
      const newMetadata: Record<string, any> = {
        ...originalMetadata,
        LeadType: values.leadType || "",
        LeadSource: values.leadSource || "",
        Interests: values.interests || [],
        HowTheyHeard: values.howTheyHeard || "",
        ReferringMember: values.referringMember || "",
      };

      const norm = (v: any) =>
        typeof v === "string" ? v.replace(/\s+/g, "") : v;
      const metadataChanged =
        (newMetadata.LeadType || "") !==
          (norm(originalMetadata.LeadType) || "") ||
        (newMetadata.LeadSource || "") !==
          (norm(originalMetadata.LeadSource) || "") ||
        JSON.stringify(newMetadata.Interests || []) !==
          JSON.stringify(originalMetadata.Interests || []) ||
        (newMetadata.HowTheyHeard || "") !==
          (norm(originalMetadata.HowTheyHeard) || "") ||
        (newMetadata.ReferringMember || "") !==
          (originalMetadata.ReferringMember || "");

      // --- Detect followup changes ---
      const lastContacted = values.lastContacted
        ? (values.lastContacted as dayjs.Dayjs).format("YYYY-MM-DD")
        : "";
      const nextFollowUpDate = values.nextFollowUpDate
        ? (values.nextFollowUpDate as dayjs.Dayjs).format("YYYY-MM-DD")
        : "";
      const notes = values.notes ?? "";
      const status = values.status ?? FollowUpStatus.Prospect;
      const contactedByStaffId =
        values.contactedByStaffId != null
          ? Number(values.contactedByStaffId)
          : null;

      const followupChanged =
        lastContacted !== (primary?.followUpDate || "") ||
        nextFollowUpDate !== (primary?.nextFollowUpDate || "") ||
        notes !== (primary?.notes || "") ||
        status !== (primary?.status ?? FollowUpStatus.Prospect) ||
        contactedByStaffId !== (primary?.contactedByStaffId ?? null);

      if (followupChanged) {
        const hasDate = lastContacted || nextFollowUpDate;
        if (!hasDate) {
          message.error(
            "At least one date (Last Contacted or Next Follow-Up) is required to save follow-up changes."
          );
          setSaving(false);
          return;
        }
      }

      // --- Persist ---
      if (metadataChanged) {
        await onSaveMember(member, newMetadata);
      }

      if (followupChanged) {
        const updatedFollowup: MemberFollowup = {
          ...(primary ?? {
            membershipId: member.membershipId,
            followUpDate: "",
            notes: "",
            status: FollowUpStatus.Prospect,
          }),
          followUpDate: lastContacted,
          nextFollowUpDate,
          notes,
          status,
          contactedByStaffId,
        };
        if (primary?.id) updatedFollowup.id = primary.id;
        updatedFollowup.membershipId = member.membershipId;

        await onSaveFollowup(member.membershipId, updatedFollowup);
      }

      if (!metadataChanged && !followupChanged) {
        message.info("No changes to save.");
      }

      onClose();
      onSaved();
    } catch (e) {
      if (e && typeof (e as any).errorFields === "undefined") {
        message.error("Failed to save.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={member ? `Edit – ${member.name}` : "Edit"}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={saving}
      okText="Save"
      destroyOnClose
      width={640}
    >
      {member && (member.emailAddress || member.phoneNumber) && (
        <div style={{ marginBottom: 16, color: "#888", fontSize: 13 }}>
          {member.emailAddress}
          {member.emailAddress && member.phoneNumber && " · "}
          {member.phoneNumber}
        </div>
      )}

      <Form form={form} layout="vertical" size="middle">
        <Divider orientation="left" plain style={{ marginTop: 0 }}>
          Lead Information
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="leadType" label="Lead Type">
              <Select
                options={LEAD_TYPE_OPTIONS}
                allowClear
                placeholder="Select lead type"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="leadSource" label="Lead Source">
              <Select
                options={LEAD_SOURCE_OPTIONS}
                allowClear
                placeholder="Select lead source"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="howTheyHeard" label="How They Heard">
              <Select
                options={howTheyHeardOptions}
                allowClear
                placeholder="Select"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="referringMember" label="Referring Member">
              <Input placeholder="Enter referring member" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="interests" label="Interests">
          <Select
            mode="multiple"
            options={INTERESTS_OPTIONS.map((i) => ({ value: i, label: i }))}
            placeholder="Select interests"
          />
        </Form.Item>

        <Divider orientation="left" plain>
          Follow-up
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="lastContacted" label="Last Contacted">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="nextFollowUpDate" label="Next Follow-Up">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="status" label="Status">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="contactedByStaffId" label="Spoke To">
              <Select
                allowClear
                placeholder="Select staff member"
                options={staffUsers.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditFollowupModal;
